/**
 * QA Review Types
 *
 * Types for the Blog QA Review Table feature.
 * ISSUE-BASED framing: checked = problem found, unchecked = OK or not reviewed.
 */

// =============================================================================
// QA Rules Definition
// =============================================================================

export const QA_RULES = [
  { key: 'issue_ext_links', label: 'Insufficient External Links', shortLabel: 'Ext Links', description: 'Fewer than 2 external links for statistics or examples' },
  { key: 'issue_citations', label: 'Bad Citation Format', shortLabel: 'Citations', description: 'Citations are parenthetical (source) instead of inline' },
  { key: 'issue_competitor_links', label: 'Competitor Links Found', shortLabel: 'Competitors', description: 'External links point to local competitors' },
  { key: 'issue_int_links', label: 'Low Internal Links', shortLabel: 'Int Links', description: 'Fewer than 3-4 internal links per 300 words' },
  { key: 'issue_kw_density', label: 'KW Density Off', shortLabel: 'Density', description: 'Keyword density outside 1-2% target range' },
  { key: 'issue_kw_first_para', label: 'KW Missing First Para', shortLabel: '1st Para', description: 'Primary keyword not in first paragraph' },
  { key: 'issue_kw_heading', label: 'KW Missing Headings', shortLabel: 'Headings', description: 'Primary keyword not in any H2 or H3' },
  { key: 'issue_meta_length', label: 'Meta Length Wrong', shortLabel: 'Meta Len', description: 'Meta description not 150-155 characters' },
  { key: 'issue_meta_keyword', label: 'Meta Missing KW', shortLabel: 'Meta KW', description: 'Meta description missing primary keyword' },
  { key: 'issue_meta_ctr', label: 'Meta Not Compelling', shortLabel: 'Meta CTR', description: 'Meta description not compelling for clicks' },
  { key: 'issue_cta', label: 'CTA Missing/Weak', shortLabel: 'CTA', description: 'No clear call-to-action directing to client' },
  { key: 'issue_structure', label: 'Structure Issues', shortLabel: 'Structure', description: 'Missing bullets, H2/H3s, or extra spacing' },
  { key: 'issue_featured_img', label: 'No Featured Image', shortLabel: 'Image', description: 'Featured image not generated' },
] as const;

export type QARuleKey = typeof QA_RULES[number]['key'];

// =============================================================================
// QA Review Model
// =============================================================================

export interface QAReview {
  id: string;
  content_id: string;
  tenant_id: string;

  // 13 issue flags (checked = problem found)
  issue_ext_links: boolean;
  issue_citations: boolean;
  issue_competitor_links: boolean;
  issue_int_links: boolean;
  issue_kw_density: boolean;
  issue_kw_first_para: boolean;
  issue_kw_heading: boolean;
  issue_meta_length: boolean;
  issue_meta_keyword: boolean;
  issue_meta_ctr: boolean;
  issue_cta: boolean;
  issue_structure: boolean;
  issue_featured_img: boolean;

  // Metadata
  reviewer_notes?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Enriched QA Review (joined with blog_content via LEFT JOIN)
// Posts without a qa_reviews row have null for all qr.* columns.
// =============================================================================

export interface QAReviewWithContent {
  // Always present (from blog_content)
  content_id: string;
  keyword?: string | null;
  title?: string | null;
  content_status?: string | null;
  publish_status?: string | null;
  wordpress_post_url?: string | null;
  total_word_count?: number | null;
  issue_count?: number | null;

  // From qa_reviews — null when no review exists
  id?: string | null;
  tenant_id?: string | null;

  // 13 issue flags — null means not yet reviewed (treat as unchecked)
  issue_ext_links: boolean | null;
  issue_citations: boolean | null;
  issue_competitor_links: boolean | null;
  issue_int_links: boolean | null;
  issue_kw_density: boolean | null;
  issue_kw_first_para: boolean | null;
  issue_kw_heading: boolean | null;
  issue_meta_length: boolean | null;
  issue_meta_keyword: boolean | null;
  issue_meta_ctr: boolean | null;
  issue_cta: boolean | null;
  issue_structure: boolean | null;
  issue_featured_img: boolean | null;

  // Metadata — null when no review exists
  reviewer_notes?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;

  // Comment counts — returned by list endpoint
  comment_count?: number | null;
  latest_comment?: string | null;
  editor_comment_count?: number | null;
}

// =============================================================================
// Request/Response Types
// =============================================================================

export type QAStatusFilter = 'all' | 'incomplete' | 'complete' | 'not_reviewed';

export interface QAReviewFilters {
  qa_status?: QAStatusFilter;
  date_from?: string;
  date_to?: string;
  search?: string;
  wp_status?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface QAReviewListResponse {
  reviews: QAReviewWithContent[];
  total: number;
  limit: number;
  offset: number;
}

export interface QAReviewUpsert {
  // Partial update: any subset of the 13 issue flags
  issue_ext_links?: boolean;
  issue_citations?: boolean;
  issue_competitor_links?: boolean;
  issue_int_links?: boolean;
  issue_kw_density?: boolean;
  issue_kw_first_para?: boolean;
  issue_kw_heading?: boolean;
  issue_meta_length?: boolean;
  issue_meta_keyword?: boolean;
  issue_meta_ctr?: boolean;
  issue_cta?: boolean;
  issue_structure?: boolean;
  issue_featured_img?: boolean;

  // Optional metadata
  reviewer_notes?: string | null;
  reviewed_by?: string | null;
}

// =============================================================================
// QA Comments
// =============================================================================

export interface QAComment {
  id: string;
  content_id: string;
  author: string;
  comment_text: string;
  block_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface QACommentCreate {
  comment_text: string;
  author?: string;
  block_id?: string | null;
}

export interface QACommentUpdate {
  comment_text: string;
}

export interface QACommentListResponse {
  comments: QAComment[];
}

// =============================================================================
// Computed Helpers
// =============================================================================

/** True if a qa_reviews row exists for this post */
export function hasQAReview(review: QAReviewWithContent): boolean {
  return review.id != null;
}

export function getIssueCount(review: QAReviewWithContent): number {
  return QA_RULES.reduce((count, rule) => {
    const val = review[rule.key as QARuleKey];
    return count + (val === true ? 1 : 0);
  }, 0);
}

export function getCheckedCount(review: QAReviewWithContent): number {
  return getIssueCount(review);
}

export function getTotalRules(): number {
  return QA_RULES.length;
}

export function isReviewComplete(review: QAReviewWithContent): boolean {
  return !!review.reviewed_at;
}
